/**
 * Date gates (docs/event-model.md §5): code that compares the current date
 * with a fixed date, so it behaves differently before and after it. One run
 * only sees one side, so analysis reports the gate and a coverage caveat.
 *
 * The event log doesn't record clock reads (they are far too common), so
 * this is a small static pass over each code layer: a comparison between a
 * clock read (`Date.now()`, `new Date()`, a variable holding one, …) and an
 * absolute date (an epoch-ms literal, `Date.parse`/`Date.UTC`/`new Date(…)`
 * of literals, a year compared with `getFullYear()`, or a constant holding
 * one). Elapsed-time checks (`Date.now() - t0 > 5000`) aren't gates. Guest
 * `Date` arithmetic is UTC (docs/sandbox.md §7), and so is this.
 */
import { type AnyNode, children, parseProgram } from "../rewrite/parse";

export interface DateGate {
  /** The comparison's line in its layer's code. */
  line: number;
  /** The date compared with, in epoch ms, when it can be worked out. */
  date?: number;
  /** The lines of the statement or expression the comparison gates. */
  region: { from: number; to: number };
}

const COMPARISONS = new Set(["<", "<=", ">", ">=", "==", "===", "!=", "!=="]);
/** Absolute dates must fall between 2000 and 2100 (epoch ms), so small numbers aren't dates. */
const MIN_DATE = Date.UTC(2000, 0, 1);
const MAX_DATE = Date.UTC(2100, 0, 1);
const GATING = new Set([
  "IfStatement",
  "ConditionalExpression",
  "LogicalExpression",
  "WhileStatement",
]);
const YEAR_GETTERS = new Set(["getFullYear", "getUTCFullYear"]);
const TIME_GETTERS = new Set(["getTime", "valueOf"]);
/** Marks a date we know is one but can't compute. */
const UNKNOWN = Number.NaN;

const isMember = (n: AnyNode | undefined, object: string, property: string) =>
  n?.type === "MemberExpression" &&
  !n.computed &&
  n.object.type === "Identifier" &&
  n.object.name === object &&
  n.property.type === "Identifier" &&
  n.property.name === property;

const methodName = (n: AnyNode): string | null =>
  n.type === "CallExpression" &&
  n.callee.type === "MemberExpression" &&
  !n.callee.computed &&
  n.callee.property.type === "Identifier"
    ? n.callee.property.name
    : null;

const isDateCtor = (n: AnyNode) =>
  n.type === "NewExpression" && n.callee.type === "Identifier" && n.callee.name === "Date";

/** `Date.parse` of an ISO date (a zone-less time counts as UTC); NaN for other formats. */
function parseIsoDate(text: string): number {
  if (!/^\d{4}-\d{2}-\d{2}/.test(text)) return UNKNOWN;
  const zoned = /T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(text) ? `${text}Z` : text;
  return Date.parse(zoned);
}

function numbers(args: AnyNode[]): number[] | null {
  const out: number[] = [];
  for (const a of args) {
    if (a.type !== "Literal" || typeof a.value !== "number") return null;
    out.push(a.value);
  }
  return out;
}

function utc(parts: number[]): number {
  const [year = 1970, month = 0, ...rest] = parts;
  return Date.UTC(year, month, ...rest);
}

interface Scope {
  dates: Map<string, number>;
  clocks: Set<string>;
}

/** The absolute date `n` evaluates to: a number, UNKNOWN (NaN) for an unparsed one, or null. */
function dateValue(n: AnyNode, scope: Scope): number | null {
  switch (n.type) {
    case "Literal":
      return typeof n.value === "number" && n.value >= MIN_DATE && n.value < MAX_DATE
        ? n.value
        : null;
    case "Identifier":
      return scope.dates.get(n.name) ?? null;
    case "UnaryExpression":
      return n.operator === "+" ? dateValue(n.argument, scope) : null;
    case "NewExpression": {
      if (!isDateCtor(n) || n.arguments.length === 0) return null;
      const [first] = n.arguments;
      if (n.arguments.length === 1 && first.type === "Literal" && typeof first.value === "string")
        return parseIsoDate(first.value);
      if (n.arguments.length === 1) return dateValue(first, scope);
      const parts = numbers(n.arguments);
      return parts ? utc(parts) : UNKNOWN;
    }
    case "CallExpression": {
      if (isMember(n.callee, "Date", "parse")) {
        const [arg] = n.arguments;
        return arg?.type === "Literal" && typeof arg.value === "string"
          ? parseIsoDate(arg.value)
          : UNKNOWN;
      }
      if (isMember(n.callee, "Date", "UTC")) {
        const parts = numbers(n.arguments);
        return parts ? utc(parts) : UNKNOWN;
      }
      const method = methodName(n);
      if (method && TIME_GETTERS.has(method)) return dateValue(n.callee.object, scope);
      return null;
    }
    default:
      return null;
  }
}

/** Whether `n` reads the current time (and isn't arithmetic on it). */
function isClockRead(n: AnyNode, scope: Scope): boolean {
  switch (n.type) {
    case "Identifier":
      return scope.clocks.has(n.name);
    case "UnaryExpression":
      return n.operator === "+" && isClockRead(n.argument, scope);
    case "NewExpression":
      return isDateCtor(n) && n.arguments.length === 0;
    case "CallExpression": {
      if (isMember(n.callee, "Date", "now")) return true;
      const method = methodName(n);
      return method !== null && TIME_GETTERS.has(method) && isClockRead(n.callee.object, scope);
    }
    default:
      return false;
  }
}

/** A year compared with `<clock>.getFullYear()`. */
function yearGate(clock: AnyNode, other: AnyNode, scope: Scope): number | null {
  const method = methodName(clock);
  if (!method || !YEAR_GETTERS.has(method) || !isClockRead(clock.callee.object, scope)) return null;
  if (other.type !== "Literal" || typeof other.value !== "number") return null;
  const year = other.value;
  return year >= 2000 && year < 2100 ? Date.UTC(year, 0, 1) : null;
}

function gateDate(node: AnyNode, scope: Scope): number | null {
  const sides: [AnyNode, AnyNode][] = [
    [node.left, node.right],
    [node.right, node.left],
  ];
  for (const [clock, other] of sides) {
    const year = yearGate(clock, other, scope);
    if (year !== null) return year;
    if (!isClockRead(clock, scope)) continue;
    const date = dateValue(other, scope);
    if (date !== null) return date;
  }
  return null;
}

/** Records `const NAME = <date>` and `const now = <clock read>` bindings, in source order. */
function bind(node: AnyNode, scope: Scope): void {
  if (node.type !== "VariableDeclarator" || node.id.type !== "Identifier" || !node.init) return;
  const date = dateValue(node.init, scope);
  if (date !== null) scope.dates.set(node.id.name, date);
  else if (isClockRead(node.init, scope)) scope.clocks.add(node.id.name);
}

function parse(code: string): AnyNode | null {
  for (const type of ["script", "module"] as const) {
    const result = parseProgram(code, type);
    if (result.program) return result.program;
  }
  return null;
}

/** Statements and functions end the condition a comparison sits in. */
const isBoundary = (n: AnyNode) =>
  /Statement$|Declaration$|^(Arrow)?Function/.test(n.type) || n.type === "Program";

/** The date gates in `code`, in source order. */
export function findDateGates(code: string): DateGate[] {
  const program = parse(code);
  if (!program) return [];
  const scope: Scope = { dates: new Map(), clocks: new Set() };
  const gates: DateGate[] = [];
  const walk = (node: AnyNode, gating: AnyNode | null) => {
    bind(node, scope);
    if (node.type === "BinaryExpression" && COMPARISONS.has(node.operator)) {
      const date = gateDate(node, scope);
      if (date !== null) {
        const region = gating ?? node;
        gates.push({
          line: node.loc?.start.line ?? 0,
          ...(!Number.isNaN(date) && { date }),
          region: { from: region.loc?.start.line ?? 0, to: region.loc?.end.line ?? 0 },
        });
      }
    }
    const test = GATING.has(node.type) ? (node.test ?? node.left) : null;
    for (const child of children(node)) {
      // A gate's region is the if/?:/while/&& whose condition holds the comparison.
      if (child === test) walk(child, node);
      else walk(child, isBoundary(child) ? null : gating);
    }
  };
  walk(program, null);
  return gates;
}
